import React, { useState, useEffect, useCallback } from 'react';
import {
  Gamepad2,
  X,
  CheckCircle2,
  Clock,
  RotateCw,
  UserPlus,
  AlertTriangle,
  AlertCircle,
  ShieldAlert,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Send,
} from 'lucide-react';
import {
  checkFriendshipStatus,
  sendBotFriendRequests,
  FriendshipCheckResponse,
  SendBotFriendRequestsResponse,
  CheckApiError,
} from '../../services/checkApi';
import { lockScroll, unlockScroll } from '../../utils/scrollLock';

export interface AccountStatusModalAccount {
  id?: number;
  username: string;
  accountId?: string;
  is_primary?: boolean;
  platform?: string;
}

export interface AccountStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  account: AccountStatusModalAccount | null;
  onStatusUpdated?: () => void;
}

export const AccountStatusModal: React.FC<AccountStatusModalProps> = ({
  isOpen,
  onClose,
  account,
  onStatusUpdated,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [statusData, setStatusData] = useState<FriendshipCheckResponse | null>(null);

  // Bot friend requests action states
  const [isSendingInvites, setIsSendingInvites] = useState<boolean>(false);
  const [sendFeedback, setSendFeedback] = useState<{
    type: 'success' | 'warning' | 'error';
    message: string;
    guide?: string[];
  } | null>(null);
  const [isPrivacyGuideOpen, setIsPrivacyGuideOpen] = useState<boolean>(true);

  // Fetch live status from POST /api/check/lookup-and-status
  const fetchLiveStatus = useCallback(
    async (targetAccount: AccountStatusModalAccount) => {
      setIsLoading(true);
      setError(null);
      setSendFeedback(null);

      try {
        const response = await checkFriendshipStatus(
          {
            username: targetAccount.username,
            account_id: targetAccount.accountId || null,
            total_vbucks: 1000,
          },
          { timeoutMs: 12000 }
        );
        setStatusData(response);
      } catch (err: any) {
        if (err instanceof CheckApiError && err.statusCode === 404) {
          setError(
            `No se encontró ningún jugador con el nombre '${targetAccount.username}' en Epic Games, PlayStation ni Xbox.`
          );
        } else if (err instanceof CheckApiError && err.statusCode === 429) {
          setError(
            'Límite de consultas excedido. Por favor espera unos momentos antes de reintentar.'
          );
        } else {
          setError(
            err.message ||
              'No pudimos conectar con los servidores de Epic Games para verificar esta cuenta.'
          );
        }
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const [isClosing, setIsClosing] = useState(false);

  const handleClose = useCallback(() => {
    if (isClosing) return;
    setIsClosing(true);
    unlockScroll();
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 160);
  }, [isClosing, onClose]);

  // Effect to load data when modal opens with an account
  useEffect(() => {
    if (isOpen && account) {
      lockScroll();
      setIsClosing(false);
      fetchLiveStatus(account);
    } else {
      unlockScroll();
      setStatusData(null);
      setError(null);
      setSendFeedback(null);
    }
    return () => {
      unlockScroll();
    };
  }, [isOpen, account, fetchLiveStatus]);

  // Keyboard navigation: Escape key closes modal
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleClose]);

  // Handle triggering bot friend requests: POST /api/check/send-all-bot-friend-requests
  const handleSendAllFriendRequests = async () => {
    if (!account) return;
    setIsSendingInvites(true);
    setSendFeedback(null);

    try {
      const response: SendBotFriendRequestsResponse = await sendBotFriendRequests(
        account.username,
        account.accountId || statusData?.user?.account_id || `epic_${account.username.toLowerCase()}`
      );

      if (response.privacy_blocked) {
        setSendFeedback({
          type: 'warning',
          message:
            response.message ||
            'Tu cuenta de Fortnite tiene bloqueada la recepción de solicitudes de amistad por configuración de privacidad.',
          guide: response.guide || [
            '1. En Fortnite, ve a Ajustes (icono de engranaje).',
            '2. Entra en la pestaña "Cuenta y Privacidad" (la última a la derecha).',
            '3. En "Privacidad Social", busca "Permiso de solicitudes de amistad".',
            '4. Cámbialo a "Cualquiera" o "Amigos de amigos" y guarda los cambios.',
          ],
        });
        setIsPrivacyGuideOpen(true);
      } else {
        const sentCount = (response.sent_bots || []).length;
        const alreadyCount = (response.already_friends_bots || []).length;
        let msg = '¡Solicitudes de amistad enviadas con éxito!';
        if (sentCount > 0) {
          msg = `Se enviaron ${sentCount} solicitud(es) de amistad desde nuestros bots oficiales. Acéptalas en Fortnite para completar la vinculación.`;
        } else if (alreadyCount > 0) {
          msg = `Ya eres amigo de todas las cuentas bot activas (${alreadyCount}).`;
        }

        setSendFeedback({
          type: 'success',
          message: msg,
        });

        // Trigger parent callback to refresh status in ProfilePage
        if (onStatusUpdated) {
          onStatusUpdated();
        }

        // Re-check live status after sending invites
        await fetchLiveStatus(account);
      }
    } catch (err: any) {
      setSendFeedback({
        type: 'error',
        message: err.message || 'Error al conectar con los bots de la tienda.',
      });
    } finally {
      setIsSendingInvites(false);
    }
  };

  if ((!isOpen && !isClosing) || !account) return null;

  // Bot counts & status interpretation
  const readyCount =
    statusData?.bots_ready_count ??
    (statusData?.status === 'FRIENDS_READY' ? statusData.accepted_bots_count ?? 1 : 0);

  const waitingCount =
    statusData?.bots_waiting_count ??
    (statusData?.status === 'FRIENDS_WAITING_48H' ? statusData.accepted_bots_count ?? 1 : 0);

  const pendingCount =
    statusData?.pending_bots_count ??
    (statusData?.status === 'REQUEST_SENT' ? 1 : 0);

  const unaddedCount =
    statusData?.unadded_bots_count ??
    (statusData?.status === 'NOT_FRIENDS' ? 1 : 0);

  const hoursRemaining = statusData?.hours_remaining ?? 0;
  const elapsedHours = Math.max(0, 48 - hoursRemaining);
  const formattedRemainingHours = Math.round(hoursRemaining * 10) / 10;
  const formattedElapsedHours = Math.round(elapsedHours * 10) / 10;
  const progressPercent = Math.min(
    100,
    Math.max(0, Math.round((elapsedHours / 48) * 100))
  );

  const hasAnySections =
    readyCount > 0 || waitingCount > 0 || pendingCount > 0 || unaddedCount > 0;

  return (
    <div
      className={`fixed inset-0 z-[200000] soft-backdrop flex items-center justify-center p-3 sm:p-4 overscroll-contain select-none ${
        isClosing ? 'anim-backdrop-out' : 'anim-backdrop-in'
      }`}
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="account-status-modal-title"
    >
      <div
        className={`w-full max-w-lg bg-white text-[#06173f] p-5 sm:p-6 border-2 border-[var(--line)] shadow-2xl relative select-none max-h-[90vh] flex flex-col ${
          isClosing ? 'anim-modal-out' : 'anim-modal-in'
        }`}
        style={{
          clipPath:
            'polygon(12px 0, 100% 0, 100% calc(100% - 12px), calc(100% - 12px) 100%, 0 100%, 0 12px)',
          filter: 'drop-shadow(8px 8px 0 rgba(6,23,63,0.25))',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button X */}
        <button
          type="button"
          onClick={handleClose}
          className="absolute top-4 right-4 sm:top-5 sm:right-5 w-8 h-8 rounded-lg bg-[var(--ice)] hover:bg-[#e2e8f0] text-[var(--soft)] hover:text-[#06173f] flex items-center justify-center transition-colors cursor-pointer z-20"
          style={{
            clipPath:
              'polygon(3px 0, 100% 0, 100% calc(100% - 3px), calc(100% - 3px) 100%, 0 100%, 0 3px)',
          }}
          title="Cerrar modal"
          aria-label="Cerrar"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Modal Header */}
        <div className="flex items-center gap-3 pr-8 pb-3 border-b-2 border-[var(--line)] shrink-0">
          <div
            className="w-10 h-10 bg-[#eff6ff] text-[#1447e6] border border-[#bfdbfe] flex items-center justify-center text-lg shrink-0"
            style={{
              clipPath:
                'polygon(4px 0, 100% 0, 100% calc(100% - 4px), calc(100% - 4px) 100%, 0 100%, 0 4px)',
            }}
          >
            <Gamepad2 className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h3
              id="account-status-modal-title"
              className="text-base sm:text-lg font-black uppercase tracking-wider text-[#06173f] font-heading truncate"
            >
              ESTADO DE LA CUENTA
            </h3>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-[var(--soft)] font-bold truncate">
                {account.username}
              </span>
              {account.is_primary && (
                <span
                  className="px-2 py-0.5 bg-[#eff6ff] text-[#1447e6] border border-[#bfdbfe] text-[9px] font-black font-heading flex items-center gap-1"
                  style={{
                    clipPath:
                      'polygon(3px 0, 100% 0, 100% calc(100% - 3px), calc(100% - 3px) 100%, 0 100%, 0 3px)',
                  }}
                >
                  PRINCIPAL
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Scrollable Content Container */}
        <div className="space-y-3.5 py-4 overflow-y-auto overscroll-contain flex-1 pr-1">
          {/* Action Feedback Banner (Success / Error / Warning) */}
          {sendFeedback && (
            <div
              className={`p-3.5 border-2 text-xs space-y-2 ${
                sendFeedback.type === 'success'
                  ? 'border-emerald-300 bg-emerald-50/70 text-emerald-900'
                  : sendFeedback.type === 'warning'
                  ? 'border-amber-300 bg-amber-50/80 text-amber-900'
                  : 'border-red-300 bg-red-50/70 text-red-900'
              }`}
              style={{
                clipPath:
                  'polygon(6px 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%, 0 6px)',
              }}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 font-bold font-heading">
                  {sendFeedback.type === 'success' ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                  ) : sendFeedback.type === 'warning' ? (
                    <ShieldAlert className="w-4 h-4 text-amber-600 shrink-0" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-red-600 shrink-0" />
                  )}
                  <span>
                    {sendFeedback.type === 'success'
                      ? 'Solicitudes Enviadas'
                      : sendFeedback.type === 'warning'
                      ? 'Privacidad en Fortnite (Solicitud Bloqueada)'
                      : 'Error de Envío'}
                  </span>
                </div>
                {sendFeedback.guide && (
                  <button
                    type="button"
                    onClick={() => setIsPrivacyGuideOpen((prev) => !prev)}
                    className="text-[11px] font-bold text-amber-700 hover:text-amber-900 flex items-center gap-1 cursor-pointer"
                  >
                    <span>{isPrivacyGuideOpen ? 'Ocultar guía' : 'Ver guía'}</span>
                    {isPrivacyGuideOpen ? (
                      <ChevronUp className="w-3.5 h-3.5" />
                    ) : (
                      <ChevronDown className="w-3.5 h-3.5" />
                    )}
                  </button>
                )}
              </div>

              <p className="leading-relaxed font-medium">{sendFeedback.message}</p>

              {/* Step-by-Step Privacy Instructions Guide */}
              {sendFeedback.guide && isPrivacyGuideOpen && (
                <div className="mt-2 pt-2 border-t border-amber-200/80 space-y-1.5 text-[11px]">
                  <p className="font-bold text-amber-950 uppercase font-heading tracking-wide">
                    Pasos para habilitar solicitudes en Fortnite:
                  </p>
                  <ol className="space-y-1 text-amber-900/90 pl-1 list-none">
                    {sendFeedback.guide.map((step, idx) => (
                      <li key={idx} className="flex items-start gap-1.5 leading-snug">
                        <span className="font-bold text-amber-700 shrink-0">
                          {idx + 1}.
                        </span>
                        <span>{step.replace(/^\d+\.\s*/, '')}</span>
                      </li>
                    ))}
                  </ol>
                  <div className="pt-2">
                    <button
                      type="button"
                      onClick={handleSendAllFriendRequests}
                      disabled={isSendingInvites}
                      className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white font-heading font-black text-[11px] uppercase tracking-wider flex items-center gap-1.5 transition cursor-pointer shadow-xs disabled:opacity-50"
                      style={{
                        clipPath:
                          'polygon(0 0, calc(100% - 4px) 0, 100% 4px, 100% 100%, 4px 100%, 0 calc(100% - 4px))',
                      }}
                    >
                      <RotateCw
                        className={`w-3 h-3 ${isSendingInvites ? 'animate-spin' : ''}`}
                      />
                      <span>Ya cambié la privacidad, reintentar</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* LOADING STATE */}
          {isLoading && (
            <div className="py-12 text-center space-y-3">
              <RotateCw className="w-8 h-8 text-[#1447e6] animate-spin mx-auto" />
              <p className="text-xs font-black uppercase tracking-wider text-[var(--soft)] font-heading">
                Consultando estado en tiempo real...
              </p>
            </div>
          )}

          {/* ERROR STATE */}
          {!isLoading && error && (
            <div
              className="p-4 bg-red-50/70 border-2 border-red-200 space-y-2 text-left"
              style={{
                clipPath:
                  'polygon(6px 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%, 0 6px)',
              }}
            >
              <div className="flex items-center gap-2 text-red-600 font-bold text-sm">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span className="font-heading uppercase">{error}</span>
              </div>
              <p className="text-xs text-[var(--soft)] leading-relaxed font-medium">
                No pudimos verificar la disponibilidad de tu cuenta en Epic Games. Asegúrate de que el nombre esté bien escrito o vuelve a intentarlo.
              </p>
              <button
                type="button"
                onClick={() => fetchLiveStatus(account)}
                className="mt-2 px-3 py-1.5 bg-white hover:bg-red-50 text-red-600 border border-red-300 text-xs font-heading font-black uppercase tracking-wider flex items-center gap-1.5 transition cursor-pointer"
                style={{
                  clipPath:
                    'polygon(0 0, calc(100% - 4px) 0, 100% 4px, 100% 100%, 4px 100%, 0 calc(100% - 4px))',
                }}
              >
                <RefreshCw className="w-3.5 h-3.5" />
                <span>Reintentar</span>
              </button>
            </div>
          )}

          {/* SUCCESS / STATUS SECTIONS */}
          {!isLoading && !error && statusData && (
            <div className="space-y-3">
              {/* ------------------------------------------------------------- */}
              {/* SECCIÓN 1: READY (+48 HORAS)                                  */}
              {/* ------------------------------------------------------------- */}
              {readyCount > 0 && (
                <div
                  className="p-4 border-2 border-emerald-300 bg-emerald-50/50 space-y-2 text-left shadow-xs transition"
                  style={{
                    clipPath:
                      'polygon(8px 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%, 0 8px)',
                  }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-emerald-800 font-heading font-black text-sm">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                      <span>
                        Eres amigo de {readyCount} bot{readyCount > 1 ? 's' : ''} hace más de 48 hs
                      </span>
                    </div>
                    <span
                      className="px-2 py-0.5 bg-emerald-100 text-emerald-800 border border-emerald-300 text-[10px] font-heading font-black shrink-0"
                      style={{
                        clipPath:
                          'polygon(3px 0, 100% 0, 100% calc(100% - 3px), calc(100% - 3px) 100%, 0 100%, 0 3px)',
                      }}
                    >
                      LISTO
                    </span>
                  </div>
                  <p className="text-xs text-[var(--soft)] leading-relaxed font-medium">
                    Tu cuenta está 100% habilitada para comprar cosméticos de la tienda y recibirlos de {readyCount > 1 ? 'estos bots' : 'este bot'} al instante.
                  </p>
                </div>
              )}

              {/* ------------------------------------------------------------- */}
              {/* SECCIÓN 2: WAITING (<48 HORAS) CON CUENTA REGRESIVA Y PROGRESO */}
              {/* ------------------------------------------------------------- */}
              {waitingCount > 0 && (
                <div
                  className="p-4 border-2 border-[#bfdbfe] bg-[#f0f6ff] space-y-2.5 text-left shadow-xs transition"
                  style={{
                    clipPath:
                      'polygon(8px 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%, 0 8px)',
                  }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-[#06173f] font-heading font-black text-sm">
                      <Clock className="w-4 h-4 text-[#1447e6] shrink-0 animate-pulse" />
                      <span>
                        Amistad en curso ({waitingCount} bot{waitingCount > 1 ? 's' : ''})
                      </span>
                    </div>
                    <span
                      className="px-2.5 py-0.5 bg-white text-[#1447e6] border border-[#bfdbfe] text-[11px] font-heading font-black shrink-0"
                      style={{
                        clipPath:
                          'polygon(3px 0, 100% 0, 100% calc(100% - 3px), calc(100% - 3px) 100%, 0 100%, 0 3px)',
                      }}
                    >
                      Faltan ~{formattedRemainingHours} hs
                    </span>
                  </div>

                  {/* Barra de Progreso Azul Simplista */}
                  <div className="space-y-1">
                    <div
                      className="w-full h-2 bg-[#dbeafe] overflow-hidden"
                      style={{
                        clipPath:
                          'polygon(2px 0, 100% 0, 100% calc(100% - 2px), calc(100% - 2px) 100%, 0 100%, 0 2px)',
                      }}
                    >
                      <div
                        className="h-full bg-[#1447e6] transition-all duration-500"
                        style={{ width: `${progressPercent}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-[10px] text-[var(--soft)] font-bold font-heading uppercase">
                      <span>{formattedElapsedHours} hs transcurridas</span>
                      <span>{formattedRemainingHours} hs restantes</span>
                    </div>
                  </div>

                  <p className="text-xs text-[var(--soft)] leading-relaxed font-medium">
                    Las entregas se habilitarán automáticamente una vez transcurridas las 48 hs.
                  </p>
                </div>
              )}

              {/* ------------------------------------------------------------- */}
              {/* SECCIÓN 3: PENDING REQUESTS (SOLICITUDES SIN ACEPTAR)         */}
              {/* ------------------------------------------------------------- */}
              {pendingCount > 0 && (
                <div
                  className="p-4 border-2 border-amber-300 bg-amber-50/50 space-y-2.5 text-left shadow-xs transition"
                  style={{
                    clipPath:
                      'polygon(8px 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%, 0 8px)',
                  }}
                >
                  <div className="flex items-center gap-2 text-amber-800 font-heading font-black text-sm">
                    <RotateCw className="w-4 h-4 text-amber-600 shrink-0" />
                    <span>
                      Tienes pendiente {pendingCount} solicitud{pendingCount > 1 ? 'es' : ''} de amistad
                    </span>
                  </div>
                  <p className="text-xs text-[var(--soft)] leading-relaxed font-medium">
                    Nuestros bots te enviaron solicitud de amistad en Fortnite. Inicia sesión en el juego o launcher de Epic Games y acéptala{pendingCount > 1 ? 's' : ''} para que empiece a correr el plazo de 48 horas.
                  </p>
                  <div className="pt-1">
                    <button
                      type="button"
                      onClick={handleSendAllFriendRequests}
                      disabled={isSendingInvites}
                      className="px-4 py-2 bg-[#1447e6] hover:bg-[#0a2a9c] text-white font-heading font-black text-xs uppercase tracking-wider flex items-center gap-1.5 transition cursor-pointer shadow-sm disabled:opacity-50"
                      style={{
                        clipPath:
                          'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))',
                      }}
                    >
                      <RotateCw
                        className={`w-3.5 h-3.5 ${isSendingInvites ? 'animate-spin' : ''}`}
                      />
                      <span>
                        {isSendingInvites
                          ? 'Enviando...'
                          : `Reenviar solicitud${pendingCount > 1 ? 'es' : ''}`}
                      </span>
                    </button>
                  </div>
                </div>
              )}

              {/* ------------------------------------------------------------- */}
              {/* SECCIÓN 4: UNADDED BOTS (BOTS DISPONIBLES PARA AGREGAR)       */}
              {/* ------------------------------------------------------------- */}
              {unaddedCount > 0 && (
                <div
                  className="p-4 border-2 border-sky-300 bg-sky-50/50 space-y-2.5 text-left shadow-xs transition"
                  style={{
                    clipPath:
                      'polygon(8px 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%, 0 8px)',
                  }}
                >
                  <div className="flex items-center gap-2 text-sky-900 font-heading font-black text-sm">
                    <UserPlus className="w-4 h-4 text-sky-600 shrink-0" />
                    <span>
                      Hay {unaddedCount} bot{unaddedCount > 1 ? 's nuevos' : ' nuevo'} para agregar
                    </span>
                  </div>
                  <p className="text-xs text-[var(--soft)] leading-relaxed font-medium">
                    Hay {unaddedCount} cuenta{unaddedCount > 1 ? 's bot oficiales adicionales disponibles' : ' bot oficial adicional disponible'} para agregar a tu cuenta y ampliar la disponibilidad de regalos.
                  </p>
                  <div className="pt-1">
                    <button
                      type="button"
                      onClick={handleSendAllFriendRequests}
                      disabled={isSendingInvites}
                      className="px-4 py-2 bg-[#1447e6] hover:bg-[#0a2a9c] text-white font-heading font-black text-xs uppercase tracking-wider flex items-center gap-1.5 transition cursor-pointer shadow-sm disabled:opacity-50"
                      style={{
                        clipPath:
                          'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))',
                      }}
                    >
                      <Send
                        className={`w-3.5 h-3.5 ${isSendingInvites ? 'animate-spin' : ''}`}
                      />
                      <span>
                        {isSendingInvites
                          ? 'Enviando...'
                          : `Enviar solicitud${unaddedCount > 1 ? 'es' : ''}`}
                      </span>
                    </button>
                  </div>
                </div>
              )}

              {/* ------------------------------------------------------------- */}
              {/* FALLBACK: SIN BOTS VINCULADOS (TODOS LOS CONTADORES EN 0)    */}
              {/* ------------------------------------------------------------- */}
              {!hasAnySections && (
                <div
                  className="p-4 border-2 border-amber-300 bg-amber-50/50 space-y-2.5 text-left shadow-xs"
                  style={{
                    clipPath:
                      'polygon(8px 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%, 0 8px)',
                  }}
                >
                  <div className="flex items-center gap-2 text-amber-800 font-heading font-black text-sm">
                    <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                    <span>Sin bots vinculados</span>
                  </div>
                  <p className="text-xs text-[var(--soft)] leading-relaxed font-medium">
                    Aún no tienes agregadas a nuestras cuentas bot oficiales en Fortnite. Envía las solicitudes de amistad para habilitar tu cuenta para recibir regalos.
                  </p>
                  <div className="pt-1">
                    <button
                      type="button"
                      onClick={handleSendAllFriendRequests}
                      disabled={isSendingInvites}
                      className="px-4 py-2 bg-[#1447e6] hover:bg-[#0a2a9c] text-white font-heading font-black text-xs uppercase tracking-wider flex items-center gap-1.5 transition cursor-pointer shadow-sm disabled:opacity-50"
                      style={{
                        clipPath:
                          'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))',
                      }}
                    >
                      <Send
                        className={`w-3.5 h-3.5 ${isSendingInvites ? 'animate-spin' : ''}`}
                      />
                      <span>
                        {isSendingInvites ? 'Enviando...' : 'Enviar solicitudes'}
                      </span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Footer Bar */}
        <div className="pt-3 border-t border-[var(--line)] flex items-center justify-between gap-2 shrink-0">
          <button
            type="button"
            onClick={() => fetchLiveStatus(account)}
            disabled={isLoading || isSendingInvites}
            className="px-3 py-2 bg-white hover:bg-slate-50 text-[var(--soft)] hover:text-[#06173f] border border-[var(--line)] text-xs font-heading font-black uppercase tracking-wider flex items-center gap-1.5 transition cursor-pointer disabled:opacity-50"
            style={{
              clipPath:
                'polygon(0 0, calc(100% - 4px) 0, 100% 4px, 100% 100%, 4px 100%, 0 calc(100% - 4px))',
            }}
          >
            <RefreshCw
              className={`w-3 h-3 ${isLoading ? 'animate-spin text-[#1447e6]' : ''}`}
            />
            <span>Actualizar</span>
          </button>

          <button
            type="button"
            onClick={handleClose}
            className="px-5 py-2 bg-slate-100 hover:bg-slate-200 text-[#06173f] border border-[var(--line)] font-heading font-black text-xs uppercase tracking-wider transition cursor-pointer"
            style={{
              clipPath:
                'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))',
            }}
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccountStatusModal;
